<script setup>
const props = defineProps({
  automations: { type: Array, default: () => [] },
  simulation: { type: Array, default: () => [] },
  busy: { type: Boolean, default: false },
});

const emit = defineEmits(['edit', 'delete', 'toggle', 'move-up', 'move-down']);

const simulationFor = id => props.simulation.find(row => row.automation_id === id);

const actionLabel = type => {
  switch (type) {
    case 'go_to_slot': return 'Ir para slot';
    case 'send_message': return 'Enviar mensagem';
    case 'notify_whatsapp': return 'Notificar WhatsApp';
    case 'change_status': return 'Alterar status';
    case 'add_label': return 'Adicionar etiqueta';
    case 'assign_agent': return 'Atribuir agente';
    case 'extract_fields': return 'Extrair fields';
    default: return type;
  }
};

const actionIcon = type => {
  switch (type) {
    case 'go_to_slot': return 'i-lucide-corner-down-right';
    case 'send_message': return 'i-lucide-send';
    case 'notify_whatsapp': return 'i-lucide-phone';
    case 'change_status': return 'i-lucide-refresh-ccw';
    case 'add_label': return 'i-lucide-tag';
    case 'assign_agent': return 'i-lucide-user-check';
    case 'extract_fields': return 'i-lucide-download';
    default: return 'i-lucide-zap';
  }
};
</script>

<template>
  <div>
    <div
      v-if="!automations.length"
      class="text-xs text-slate-500 italic py-4 text-center border border-dashed border-slate-300 dark:border-slate-600 rounded"
    >
      {{ $t('OMNI.SLOTS.API_AUTOMATIONS.EMPTY', 'Nenhuma automação configurada ainda.') }}
    </div>
    <ul
      v-else
      class="space-y-2"
    >
      <li
        v-for="(automation, idx) in automations"
        :key="automation.id"
        class="flex items-center gap-3 p-2 border border-slate-200 dark:border-slate-700 rounded bg-white dark:bg-slate-800"
        :class="{ 'opacity-60': !automation.enabled }"
      >
        <div class="flex flex-col">
          <button
            type="button"
            class="p-0.5 text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 disabled:opacity-30"
            :disabled="idx === 0 || busy"
            @click="emit('move-up', automation)"
          >
            <span class="i-lucide-chevron-up w-3 h-3" />
          </button>
          <button
            type="button"
            class="p-0.5 text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 disabled:opacity-30"
            :disabled="idx === automations.length - 1 || busy"
            @click="emit('move-down', automation)"
          >
            <span class="i-lucide-chevron-down w-3 h-3" />
          </button>
        </div>

        <span
          class="w-4 h-4"
          :class="actionIcon(automation.action_type)"
        />

        <div class="flex-1 min-w-0">
          <div class="flex items-center gap-2">
            <span class="text-sm font-medium text-slate-800 dark:text-slate-100 truncate">
              {{ automation.name }}
            </span>
            <span
              v-if="simulationFor(automation.id)?.matched"
              class="px-1.5 py-0.5 text-[10px] rounded bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300"
            >
              {{ $t('OMNI.SLOTS.API_AUTOMATIONS.WOULD_FIRE', 'Dispararia') }}
            </span>
            <span
              v-else-if="simulationFor(automation.id)"
              class="px-1.5 py-0.5 text-[10px] rounded bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300"
            >
              {{ $t('OMNI.SLOTS.API_AUTOMATIONS.WOULD_NOT_FIRE', 'Não dispararia') }}
            </span>
          </div>
          <div class="text-[11px] text-slate-500 dark:text-slate-400 truncate">
            {{ actionLabel(automation.action_type) }} · {{ automation.description }}
          </div>
        </div>

        <label class="inline-flex items-center gap-1 text-[11px] text-slate-600 dark:text-slate-300">
          <input
            type="checkbox"
            :checked="automation.enabled"
            :disabled="busy"
            @change="emit('toggle', automation)"
          />
          Ativa
        </label>
        <button
          type="button"
          class="p-1 text-slate-400 hover:text-blue-500"
          :disabled="busy"
          @click="emit('edit', automation)"
        >
          <span class="i-lucide-pencil w-3.5 h-3.5" />
        </button>
        <button
          type="button"
          class="p-1 text-slate-400 hover:text-red-500"
          :disabled="busy"
          @click="emit('delete', automation)"
        >
          <span class="i-lucide-trash-2 w-3.5 h-3.5" />
        </button>
      </li>
    </ul>
  </div>
</template>
